import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../hooks/useSupabase';
import supabase from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiSettings,
  FiUsers,
  FiCalendar,
  FiFileText,
  FiGrid,
  FiAward,
  FiImage,
  FiLogOut,
  FiHome,
  FiTrendingUp,
  FiDollarSign,
  FiMail,
  FiTool,
  FiEdit,
  FiUserPlus,
  FiShield,
  FiSend,
  FiActivity,
  FiCheckCircle,
  FiAlertTriangle,
  FiRefreshCw,
} = FiIcons;

const COMPETITION_CONTEXT = {
  season: '2026-27',
  seniorGroup: '8ος',
  k17Group: '4ος',
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthError, setHealthError] = useState(null);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { name: 'Γενικές Ρυθμίσεις', icon: FiSettings, path: '/admin/settings' },
    { name: 'Φόρμες & Anti-spam', icon: FiSend, path: '/admin/forms-settings' },
    { name: 'Διαχείριση Χρηστών', icon: FiShield, path: '/admin/users' },
    { name: 'Διαχείριση Περιεχομένου', icon: FiEdit, path: '/admin/content' },
    { name: 'Λειτουργία Συντήρησης', icon: FiTool, path: '/admin/maintenance' },
    { name: 'Μέλη Ομάδας', icon: FiUsers, path: '/admin/team' },
    { name: 'Αγώνες', icon: FiCalendar, path: '/admin/matches' },
    { name: 'Βαθμολογία', icon: FiTrendingUp, path: '/admin/standings' },
    { name: 'Νέα & Ανακοινώσεις', icon: FiFileText, path: '/admin/news' },
    { name: 'Ακαδημίες', icon: FiGrid, path: '/admin/academy' },
    { name: 'Εγγραφές Ακαδημίας', icon: FiUserPlus, path: '/admin/academy-registrations' },
    { name: 'Τουρνουά', icon: FiAward, path: '/admin/tournaments' },
    { name: 'Πολυμέσα', icon: FiImage, path: '/admin/gallery' },
    { name: 'Χορηγοί', icon: FiDollarSign, path: '/admin/sponsors' },
    { name: 'Επικοινωνίες', icon: FiMail, path: '/admin/contact-forms' },
  ];

  const fetchContentHealth = async () => {
    setHealthLoading(true);
    setHealthError(null);

    try {
      const today = new Date().toISOString().slice(0, 10);

      const [siteResult, membersResult, sponsorsResult, newsResult, matchesResult, standingsResult] = await Promise.all([
        supabase.from('site_config_aox1234').select('email').limit(1),
        supabase.from('team_members_aox1234').select('image_url,is_staff,staff_group,is_active,is_published'),
        supabase.from('sponsors_aox1234').select('url'),
        supabase.from('news_aox1234').select('image,image_url,status,published_at'),
        supabase.from('matches_aox1234').select('id,date,status').gte('date', today),
        supabase.from('standings_aox1234').select('season_year'),
      ]);

      const firstError = [siteResult, membersResult, sponsorsResult, newsResult, matchesResult, standingsResult].find((result) => result.error)?.error;
      if (firstError) throw firstError;

      const siteConfig = siteResult.data?.[0] || {};
      const members = membersResult.data || [];
      const sponsors = sponsorsResult.data || [];
      const news = newsResult.data || [];
      const matches = matchesResult.data || [];
      const seasonYears = [...new Set((standingsResult.data || []).map((row) => row.season_year).filter(Boolean))].sort().reverse();

      const playersMissingPhoto = members.filter((member) =>
        !member.is_staff && member.is_active !== false && member.is_published !== false && !member.image_url
      ).length;

      const boardMissingPhoto = members.filter((member) =>
        member.is_staff && member.staff_group === 'board' && member.is_published !== false && !member.image_url
      ).length;

      const newsWithoutImage = news.filter((item) =>
        (item.status || 'published') !== 'draft' && !(item.image || item.image_url)
      ).length;

      const sponsorsMissingUrl = sponsors.filter((sponsor) => !sponsor.url?.trim()).length;
      const upcomingMatches = matches.length;
      const detectedSeason = seasonYears[0] || null;

      setHealth({
        detectedSeason,
        officialEmail: siteConfig.email?.trim() || '',
        playersMissingPhoto,
        boardMissingPhoto,
        newsWithoutImage,
        sponsorsMissingUrl,
        upcomingMatches,
      });
    } catch (err) {
      console.error('Content health error:', err);
      setHealthError(err.message || 'Αδυναμία ελέγχου περιεχομένου');
    } finally {
      setHealthLoading(false);
    }
  };

  useEffect(() => {
    fetchContentHealth();
  }, []);

  const healthRows = health ? [
    {
      label: 'Τρέχουσα σεζόν',
      value: health.detectedSeason || COMPETITION_CONTEXT.season,
      ok: (health.detectedSeason || COMPETITION_CONTEXT.season) === COMPETITION_CONTEXT.season,
      path: '/admin/standings',
    },
    { label: "Όμιλος Α' Ομάδας", value: COMPETITION_CONTEXT.seniorGroup, ok: true, path: '/admin/matches' },
    { label: 'Όμιλος Κ17', value: COMPETITION_CONTEXT.k17Group, ok: true, path: '/admin/matches' },
    {
      label: 'Νέα χωρίς εικόνα',
      value: health.newsWithoutImage === 0 ? 'Κανένα' : `${health.newsWithoutImage}`,
      ok: health.newsWithoutImage === 0,
      path: '/admin/news',
    },
    {
      label: 'Επίσημο email',
      value: health.officialEmail || 'Λείπει',
      ok: Boolean(health.officialEmail),
      path: '/admin/settings',
    },
    {
      label: 'Παίκτριες χωρίς φωτογραφία',
      value: health.playersMissingPhoto === 0 ? 'Καμία' : `${health.playersMissingPhoto}`,
      ok: health.playersMissingPhoto === 0,
      path: '/admin/team',
    },
    {
      label: 'ΔΣ χωρίς φωτογραφία',
      value: health.boardMissingPhoto === 0 ? 'Κανένα' : `${health.boardMissingPhoto}`,
      ok: health.boardMissingPhoto === 0,
      path: '/admin/team',
    },
    {
      label: 'Χορηγοί χωρίς URL',
      value: health.sponsorsMissingUrl === 0 ? 'Κανένας' : `${health.sponsorsMissingUrl}`,
      ok: health.sponsorsMissingUrl === 0,
      path: '/admin/sponsors',
    },
    {
      label: 'Επόμενοι αγώνες',
      value: health.upcomingMatches === 0 ? 'Δεν υπάρχουν καταχωρημένοι' : `${health.upcomingMatches}`,
      ok: health.upcomingMatches > 0,
      path: '/admin/matches',
    },
  ] : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-primary-700 text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center gap-4">
            <div className="flex items-center space-x-4">
              <Link to="/" className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xl">ΑΟΧ</span>
              </Link>
              <div>
                <h1 className="text-xl font-bold">Διαχείριση Α.Ο. Χανιά</h1>
                <p className="text-sm text-primary-200">Συνδεδεμένος ως {user?.email}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Link to="/" className="flex items-center bg-primary-600 hover:bg-primary-800 text-white px-4 py-2 rounded-lg text-sm" target="_blank" rel="noopener noreferrer">
                <SafeIcon icon={FiHome} className="mr-2 h-4 w-4" />
                Αρχική Σελίδα
              </Link>
              <button onClick={handleLogout} className="flex items-center bg-primary-600 hover:bg-primary-800 text-white px-4 py-2 rounded-lg text-sm">
                <SafeIcon icon={FiLogOut} className="mr-2 h-4 w-4" />
                Αποσύνδεση
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h2 className="text-2xl font-bold text-gray-900 mb-8">Πίνακας Διαχείρισης</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {menuItems.map((item, index) => (
            <motion.div key={item.name} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.05 }}>
              <Link to={item.path} className="block bg-white rounded-xl shadow-lg hover:shadow-xl p-6 transition-shadow duration-200">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center"><SafeIcon icon={item.icon} className="h-6 w-6 text-primary-600" /></div>
                  <div><h3 className="text-lg font-semibold text-gray-900">{item.name}</h3><p className="text-sm text-gray-500">Διαχείριση</p></div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiActivity} className="w-5 h-5 text-primary-700" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Content Health</h3>
                <p className="text-sm text-gray-500">Αυτόματος έλεγχος βασικού περιεχομένου πριν τη δημοσίευση.</p>
              </div>
            </div>
            <button onClick={fetchContentHealth} disabled={healthLoading} className="inline-flex items-center px-3 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 mr-2 ${healthLoading ? 'animate-spin' : ''}`} />
              Ανανέωση
            </button>
          </div>

          {healthLoading ? (
            <div className="p-8 text-center text-gray-500">Έλεγχος περιεχομένου...</div>
          ) : healthError ? (
            <div className="p-6 bg-red-50 text-red-700">{healthError}</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {healthRows.map((row) => (
                <Link key={row.label} to={row.path} className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-6 py-4 hover:bg-gray-50 transition-colors">
                  <SafeIcon icon={row.ok ? FiCheckCircle : FiAlertTriangle} className={`w-5 h-5 ${row.ok ? 'text-green-600' : 'text-amber-600'}`} />
                  <span className="text-sm font-medium text-gray-800">{row.label}</span>
                  <span className={`text-sm text-right ${row.ok ? 'text-green-700' : 'text-amber-700'}`}>{row.value}</span>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="mt-10 bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Γρήγορες Συμβουλές</h3>
          <ul className="space-y-2 text-gray-600">
            <li className="flex items-start"><div className="w-2 h-2 bg-primary-600 rounded-full mt-2 mr-3"></div><span>Χρησιμοποιήστε τη λειτουργία συντήρησης όταν κάνετε σημαντικές αλλαγές</span></li>
            <li className="flex items-start"><div className="w-2 h-2 bg-primary-600 rounded-full mt-2 mr-3"></div><span>Οι δημόσιες φόρμες και το reCAPTCHA διαχειρίζονται από το «Φόρμες & Anti-spam»</span></li>
            <li className="flex items-start"><div className="w-2 h-2 bg-primary-600 rounded-full mt-2 mr-3"></div><span>Τα νέα μπορούν πλέον να αποθηκεύονται ως πρόχειρα, δημοσιευμένα ή προγραμματισμένα.</span></li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
